import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../../../../api/client';
import { Btn, ErrorLine, Icon, InfoLine, Seg, TextInput } from '../kit';
import { font as f } from '../style';
import { C } from '../tokens';
import { ListPicker } from './ListPicker';
import { PRESET_IDS, PRESETS, money, seedSpend, usernameOf, type PresetId } from './model';

/** Create a Sandbox Chat: who the fan is, what history they start with, which lists they're in. */
export function NewChatDialog({ accountId, lists, onRefreshLists, onClose, onCreated }: { accountId: string; lists: string[]; onRefreshLists?: () => Promise<unknown>; onClose: () => void; onCreated: (fanId: string) => void }) {
  const { t } = useTranslation('assistantCompact');
  const [name, setName] = useState('');
  const [preset, setPreset] = useState<PresetId>('new');
  const [picked, setPicked] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const k = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', k);
    return () => window.removeEventListener('keydown', k);
  }, [onClose]);

  const create = async () => {
    const fanName = name.trim() || 'Tom';
    setBusy(true);
    setError(null);
    try {
      const r = await api.createSandboxChat({
        account_id: accountId,
        profile: { name: fanName, displayName: fanName, username: usernameOf(fanName), preset },
        lists: picked,
        seed: PRESETS[preset],
      });
      onCreated(r.fan_id);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  };

  return createPortal(
    <div className="cpt-root" onMouseDown={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }} style={{ position: 'fixed', inset: 0, zIndex: 1200, background: 'color-mix(in srgb, var(--om-page) 72%, transparent)', display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '40px 16px', boxSizing: 'border-box' }}>
      <div role="dialog" aria-modal="true" aria-label={t('sb.dlg.title')} style={{ width: 460, maxWidth: '100%', maxHeight: '100%', background: C.card, border: `1px solid ${C.borderHi}`, borderRadius: 10, boxShadow: '0 16px 48px rgba(0,0,0,.55)', display: 'flex', flexDirection: 'column' }}>
        <div style={{ height: 48, flex: 'none', display: 'flex', alignItems: 'center', gap: 10, padding: '0 10px 0 16px', borderBottom: `1px solid ${C.divider}` }}>
          <Icon name="flask" size={15} color={C.purpleLt} />
          <span style={{ font: f(600, 14), color: C.text, flex: 1 }}>{t('sb.dlg.title')}</span>
          <button type="button" onClick={onClose} title={t('close')} className="cpt-hover-row" style={{ width: 28, height: 28, border: 0, borderRadius: 6, background: 'transparent', color: C.sub, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Icon name="x" size={15} />
          </button>
        </div>

        <div className="cpt-thin-scroll" style={{ flex: 1, minHeight: 0, overflow: 'auto', padding: '14px 16px 16px', display: 'flex', flexDirection: 'column', gap: 16 }}>
          <Field label={t('sb.dlg.name')}>
            <TextInput value={name} onChange={setName} placeholder={t('sb.dlg.namePh')} />
          </Field>
          <Field label={t('sb.dlg.start')} hint={t(`sb.dlg.startHint.${preset}`, { spent: money(seedSpend(PRESETS[preset])) })}>
            <div style={{ alignSelf: 'flex-start' }}>
              <Seg options={PRESET_IDS.map((id) => ({ id, label: t(`sb.presets.${id}`) }))} value={preset} onChange={setPreset} />
            </div>
          </Field>
          <Field label={t('sb.dlg.lists')} hint={t('sb.dlg.listsHint')}>
            <ListPicker all={lists} value={picked} onChange={setPicked} onRefresh={onRefreshLists} />
          </Field>
          <InfoLine>{t('sb.costNote')}</InfoLine>
          {error && <ErrorLine>{error}</ErrorLine>}
        </div>

        <div style={{ flex: 'none', display: 'flex', justifyContent: 'flex-end', gap: 8, padding: '10px 16px', borderTop: `1px solid ${C.divider}` }}>
          <Btn onClick={onClose} disabled={busy}>{t('cancel')}</Btn>
          <Btn kind="primary" icon="plus" onClick={() => void create()} disabled={busy}>{t('sb.dlg.create')}</Btn>
        </div>
      </div>
    </div>,
    document.body,
  );
}

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <span style={{ font: f(500, 13), color: C.text }}>{label}</span>
        {hint && <span style={{ font: f(400, 11.5), color: C.sub }}>{hint}</span>}
      </div>
      {children}
    </div>
  );
}
